import { type Href, router } from 'expo-router';
import { BookNextSessionWidget } from 'smoothie';

import { useAuthedUser } from '../../common/providers/AuthedUserProvider';
import { useMembershipGuidanceSessionInfo } from '../hooks/useMembershipGuidanceSessionInfo';
import { GuidanceSessionType } from '../types/GuidanceSessions';

// Allow navigation to either the large details page (book-session-details) or the calendly page (book-session).
type BookingRoute =
    | '/guidance-sessions/book-session-details'
    | '/guidance-sessions/book-session';

const BookSessionPrompt = ({
    sessionType,
    title,
    description,
    buttonLabel,
    enableBooking = true,
    bookingRoute = '/guidance-sessions/book-session-details',
    doneRoute = '/',
    maxWidth,
}: {
    sessionType: GuidanceSessionType;
    title?: string;
    description?: string;
    buttonLabel?: string;
    enableBooking?: boolean;
    bookingRoute?: BookingRoute;
    doneRoute?: Href;
    maxWidth?: number;
}) => {
    const user = useAuthedUser();
    const { getSessionDetails } = useMembershipGuidanceSessionInfo();
    const guide = user?.membership?.guide;
    const guideName = guide?.firstName ?? 'your Guide';
    const guideImageUrl = guide?.profileImageUrl ?? undefined;
    const { title: sessionName } = getSessionDetails(sessionType);

    const sessionTitle = title ?? `Book your ${sessionName} session`;
    const sessionDescription =
        description ??
        `You're ready to schedule your ${sessionName} session with ${guideName}.`;
    buttonLabel =
        buttonLabel ??
        (sessionType === GuidanceSessionType.DISCOVERY
            ? 'Book your first session'
            : 'Book your next session');

    return (
        <BookNextSessionWidget
            title={sessionTitle}
            description={sessionDescription}
            buttonLabel={buttonLabel}
            guideImageUrl={guideImageUrl}
            enableBooking={enableBooking}
            onPress={() => {
                router.navigate({
                    pathname: bookingRoute,
                    params: {
                        sessionType,
                        doneRoute: doneRoute as string,
                    },
                });
            }}
            maxWidth={maxWidth}
        />
    );
};

export default BookSessionPrompt;
